<template>
  <div id="binancePayMethod">
    <div class="binance-container">
      <!-- Header -->
      <div class="header-section">
        <div class="binance-icon">
          <svg
            width="24"
            height="24"
            viewBox="0 0 126.61 126.61"
            fill="#F3BA2F"
          >
            <path
              d="M38.73 53.2l24.59-24.58 24.6 24.6 14.3-14.31L63.32 0 24.42 38.9l14.31 14.3zM0 63.31l14.3-14.3 14.31 14.3-14.3 14.3zM38.73 73.41l24.59 24.59 24.6-24.6 14.31 14.29-38.9 38.91-38.91-38.9 14.31-14.3zM98 63.31l14.3-14.3 14.31 14.3-14.3 14.3z"
            />
            <path
              d="M77.83 63.3L63.32 48.78 52.59 59.51l-1.24 1.23-2.54 2.54 14.51 14.5 14.51-14.47z"
              fill="#F3BA2F"
            />
          </svg>
        </div>
        <h4 class="method-title">Binance Pay USDT / USDC INSTANT</h4>
      </div>

      
      <!-- Instructions -->
      <div class="instructions-box">
        <div class="instructions-header">
          <feather-icon icon="InfoIcon" size="16" />
          <span>How to complete payment:</span>
        </div>
        <ol class="instructions-list">
          <li>Copy the Binance Pay ID below</li>
          <li>Open your Binance app and go to "Pay"</li>
          <li>Send your desired amount to the Pay ID</li>
          <li>Copy the Order ID from your transaction</li>
          <li>Paste it below and click "Verify Payment"</li>
        </ol>
      </div>

      <!-- Binance Pay ID Section -->
      <div class="pay-id-section">
        <label class="label-text">
          <feather-icon icon="CreditCardIcon" size="14" class="mr-1" />
          Binance Pay ID:
        </label>
        <div class="pay-id-card">
          <div class="pay-id-value">{{ binancePayId }}</div>
          <b-button
            v-ripple.400="'rgba(115, 103, 240, 0.15)'"
            size="sm"
            @click="copyPayId"
            class="copy-btn"
            variant="outline-info"
          >
            <feather-icon :icon="copied ? 'CheckIcon' : 'CopyIcon'" size="14" />
            {{ copied ? "Copied!" : "Copy" }}
          </b-button>
        </div>
      </div>

      <!-- Order ID Input -->
      <div class="order-section">
        <label class="label-text">
          <feather-icon icon="HashIcon" size="14" class="mr-1" />
          Transaction Order ID:
        </label>
        <b-form-input
          v-model="orderId"
          placeholder="Enter your Binance transaction Order ID..."
          class="order-input"
          :disabled="isProcessing"
        />
      </div>

      <!-- Submit Button -->
      <b-button
        v-ripple.400="'rgba(115, 103, 240, 0.15)'"
        variant="outline-primary"
        block
        @click="verifyPayment"
        :disabled="!orderId || isProcessing"
        class="verify-btn"
      >
        <b-spinner small v-if="isProcessing" class="mr-2" />

        {{ isProcessing ? "Verifying Transaction..." : "Verify Transaction" }}
      </b-button>
    </div>
  </div>
</template>

<script>
import { mapMutations } from "vuex";
import { BButton, BFormInput, BSpinner } from "bootstrap-vue";
import Ripple from "vue-ripple-directive";
import Deposit from "@/services/deposit.service.js";

export default {
  components: {
    BButton,
    BFormInput,
    BSpinner,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      binancePayId: "88430628",
      orderId: "",
      copied: false,
      isProcessing: false,
    };
  },
  methods: {
    ...mapMutations(["HANDLE_CREDIT"]),

    copyPayId() {
      navigator.clipboard.writeText(this.binancePayId).then(() => {
        this.copied = true;
        setTimeout(() => {
          this.copied = false;
        }, 2000);
      });
    },

    async verifyPayment() {
      if (!this.orderId.trim()) {
        this.$swal({
          icon: "warning",
          title: "Please enter Order ID",
          text: "Order ID is required to verify your payment",
          showConfirmButton: true,
        });
        return;
      }

      this.isProcessing = true;

      try {
        const data = await Deposit.binance(this.orderId.trim());
        if (data.status === "success") {
          this.HANDLE_CREDIT(+data.credit);
          this.orderId = "";
        }

        this.$swal({
          icon: data.status,
          html: data.title,
          showConfirmButton: data.status === "error",
          showCloseButton: true,
          timer: data.status === "success" ? 3000 : undefined,
        });
      } catch (error) {
        console.error("Binance Pay verification error:", error);
        this.$swal({
          icon: "error",
          title: "Verification Failed",
          text: "Unable to verify payment. Please try again or contact support.",
          showConfirmButton: true,
        });
      } finally {
        this.isProcessing = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
#binancePayMethod {
  width: 100%;
}

.binance-container {
  border: 1px dashed #d5d5d5;
  padding: 20px;
  background: #fafafa;
  border-radius: 8px;
}

.header-section {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e8e8e8;

  .binance-icon {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .method-title {
    margin: 0;
    font-size: 1.05rem;
    font-weight: 600;
    color: #333;
  }
}

.instructions-box {
  background: #e5e5e5;
  padding: 14px 16px;
  border-radius: 6px;
  margin-bottom: 16px;

  .instructions-header {
    display: flex;
    align-items: center;
    gap: 6px;
    color: #000;
    font-weight: 600;
    font-size: 0.88rem;
    margin-bottom: 8px;
  }

  .instructions-list {
    margin: 0;
    padding-left: 18px;
    color: #000;
    font-size: 0.84rem;

    li {
      margin-bottom: 4px;
      line-height: 1.5;
      opacity: 0.95;

      &:last-child {
        margin-bottom: 0;
      }
    }
  }
}

.pay-id-section {
  margin-bottom: 14px;

  .label-text {
    display: flex;
    align-items: center;
    font-size: 0.88rem;
    font-weight: 600;
    color: #555;
    margin-bottom: 8px;
  }

  .pay-id-card {
    display: flex;
    gap: 8px;
    align-items: stretch;
    background: #fff;
    padding: 10px 12px;
    border-radius: 6px;
    border: 2px solid #575757;
  }

  .pay-id-value {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.2rem;
    font-weight: 700;
    color: #333;
    font-family: "Courier New", monospace;
    letter-spacing: 1px;
  }

  .copy-btn {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 6px 14px;
    font-weight: 600;
    white-space: nowrap;
    // background-color: #333 !important;
    border: 1px solid #5c8cfd !important;
    color: #5c8cfd !important;

    &:hover {
      background-color: #5c8cfd !important;
      color: #fff !important;
    }
  }
}

.order-section {
  margin-bottom: 14px;

  .label-text {
    display: flex;
    align-items: center;
    font-size: 0.88rem;
    font-weight: 600;
    color: #555;
    margin-bottom: 8px;
  }

  .order-input {
    background: #fff !important;
    border: 1px solid #d0d0d0 !important;
    border-radius: 6px !important;
    color: #333 !important;
    font-size: 0.9rem;
    padding: 10px 12px !important;
    height: auto !important;

    &:focus {
      border-color: #7367f0 !important;
      box-shadow: 0 0 0 2px rgba(115, 103, 240, 0.15) !important;
    }

    &::placeholder {
      color: #999;
    }
  }

  .helper-text {
    display: flex;
    align-items: center;
    gap: 4px;
    color: #888;
    font-size: 0.8rem;
    margin-top: 5px;
  }
}

.verify-btn {
  margin-bottom: 12px;
  padding: 10px;
  font-weight: 600;
  font-size: 0.95rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  color: #5c8cfd !important;
  border: 1px solid #5c8cfd !important;

  &:hover {
    border: 1px solid rgb(93, 141, 255) !important;

    color: rgb(57, 39, 255) cfd !important;
  }
  &:hover:not(:disabled) {
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

@media (max-width: 576px) {
  .binance-container {
    padding: 16px;
  }

  .pay-id-card {
    flex-direction: column;

    .copy-btn {
      width: 100%;
      justify-content: center;
    }
  }
}
</style>
